import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { AccessTime, Email, LocationOn } from '@mui/icons-material';
import { Button, Grid, Icon, Tooltip, Typography } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';

import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import DELETE_MUTATION from 'gql/mutations/InternationalOffice/deleteIOEvent.gql';
import ENROLL_MUTATION from 'gql/mutations/InternationalOffice/enrollIOEvent.gql';
import UNENROLL_MUTATION from 'gql/mutations/InternationalOffice/unenrollIOEvent.gql';
import ENROLLED_QUERY from 'gql/queries/InternationalOffice/getEnrolledIOEvents.gql';
import QUERY_DEFAULT from 'gql/queries/InternationalOffice/getIOEvents.gql';
import QUERY_FRAME from 'gql/queries/InternationalOffice/getIOEventsFrame.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';

import { TypedGridColumns } from 'app/components';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { DataGrid } from 'app/components/DataGrid';
import ErrorBoundary from 'app/components/ErrorBoundary';
import GQLGuard from 'app/components/GQLGuard';
import { Modal } from 'app/components/Modal';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { IOEvent as IOEventType } from 'types/AppTypes';
import { ACLHelper } from 'utils/ACLHelper';
import { formatMonetaryValue } from 'utils/formatMonetaryValue';

import { IOEvent } from '../../../IOEvent';

interface IOEventsListProps {
  iframe?: boolean;
  showOnlyEnrolled?: boolean;
}

export function IOEventsList({ iframe, showOnlyEnrolled }: IOEventsListProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const client = useApolloClient();
  const QUERY = iframe ? QUERY_FRAME : showOnlyEnrolled ? ENROLLED_QUERY : QUERY_DEFAULT;

  const keywordState = useState();
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });
  const [editModalEvent, setEditModalEvent] = useState<null | Object>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState<null | string>(null);
  const [disabledCancel, setDisabledCancel] = useState(false);

  const [enrollIOEvent] = useMutation(ENROLL_MUTATION);
  const [unenrollIOEvent] = useMutation(UNENROLL_MUTATION);
  const [deleteIOEvent] = useMutation(DELETE_MUTATION);

  const eventsQuery = useQuery<
    any,
    {
      offset?: number;
      limit?: number;
    }
  >(QUERY, { variables: { offset: 0, limit: paginationModel.pageSize }, fetchPolicy: 'no-cache' });
  const enrolledEventsQuery = useQuery<
    any,
    {
      offset?: number;
      limit?: number;
      enrolled?: boolean;
    }
  >(ENROLLED_QUERY, {
    variables: { offset: undefined, limit: undefined, enrolled: true },
    fetchPolicy: 'no-cache',
    skip: iframe
  });
  const { refetch } = eventsQuery;

  let aclHelper: ACLHelper | undefined;
  if (!iframe) {
    const { currentUser } = client.readQuery({ query: USER_QUERY });
    aclHelper = new ACLHelper(currentUser);
  }

  const handlePagination = useCallback(
    (event, page: number) => {
      const offset = page * paginationModel.pageSize;
      refetch({ offset: offset });
    },
    [paginationModel.pageSize, refetch]
  );

  const handlePaginationChange = useCallback(pagination => {
    setPaginationModel(pagination);
  }, []);

  const isEnrolled = useCallback(
    eventId =>
      enrolledEventsQuery.data?.ioEvents.find(enrolledEvent => enrolledEvent.id === eventId),
    [enrolledEventsQuery.data]
  );

  const filters: any[] | undefined = [
    {
      field: 'keyword',
      label: t(translations.pages.internationalOffice.users.search()),
      stateHandler: keywordState
    }
  ];

  const handleDelete = async eventId => {
    try {
      await deleteIOEvent({
        variables: {
          id: eventId
        },
        refetchQueries: ['GetIOEvents']
      });
      dispatch(
        AlertActions.success(
          t(translations.pages.internationalOffice.events.alerts.success.deletedEvent())
        )
      );
    } catch (err) {
      dispatch(
        AlertActions.error(
          t(translations.pages.internationalOffice.events.alerts.error.deletingEvent())
        )
      );
    }
  };

  const handleEnroll = useCallback(
    async eventId => {
      try {
        await enrollIOEvent({
          variables: { id: eventId },
          refetchQueries: ['GetIOEvents', 'GetEnrolledIOEvents']
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.internationalOffice.courses.alerts.success.enrolledCourse())
          )
        );
      } catch (err) {
        dispatch(
          AlertActions.error(
            t(translations.pages.internationalOffice.courses.alerts.error.enrollingCourse())
          )
        );
      }
    },
    [dispatch, enrollIOEvent, t]
  );

  const handleUnenroll = useCallback(
    async eventId => {
      try {
        await unenrollIOEvent({
          variables: { id: eventId },
          refetchQueries: ['GetIOEvents', 'GetEnrolledIOEvents']
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.internationalOffice.courses.alerts.success.unenrolledCourse())
          )
        );
      } catch (err) {
        dispatch(
          AlertActions.error(
            t(translations.pages.internationalOffice.courses.alerts.error.unenrollingCourse())
          )
        );
      }
    },
    [dispatch, t, unenrollIOEvent]
  );

  const getIoAdminActions = useCallback(
    params => {
      return [
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-trash-alt"
              style={{
                width: 'auto',
                color: 'red'
              }}
            />
          }
          label={t(translations.common.delete())}
          onClick={() => setDeleteModalOpen(params.id)}
          showInMenu
        />,
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-edit"
              style={{
                width: 'auto',
                color: 'lightblue'
              }}
            />
          }
          label={t(translations.common.edit())}
          onClick={() => setEditModalEvent(params.row)}
          showInMenu
        />,
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-file-pdf"
              style={{
                width: 'auto'
              }}
            />
          }
          label={t(translations.common.export())}
          onClick={() =>
            window.open(`${import.meta.env.VITE_GATEWAY_URL}/reports/events/${params.id}`, '_blank')
          }
          showInMenu
        />
      ];
    },
    [t, setEditModalEvent, setDeleteModalOpen]
  );

  const columns = useMemo((): TypedGridColumns<IOEventType> => {
    //@ts-ignore
    return [
      !iframe &&
        !aclHelper?.isIncomingsAdmin() && {
          width: 110,
          headerName: t(translations.common.actions()),
          field: 'actions',
          type: 'actions',
          renderCell: params => {
            return isEnrolled(params.row.id) ? (
              <Button
                size="small"
                color="error"
                onClick={() => handleUnenroll(params.row.id)}
                variant="outlined"
                autoCapitalize="false"
                sx={{ textTransform: 'none' }}
              >
                {t(translations.pages.internationalOffice.courses.unenroll())}
              </Button>
            ) : (
              <Button
                size="small"
                color="success"
                onClick={() => handleEnroll(params.row.id)}
                variant="outlined"
                sx={{ textTransform: 'none' }}
              >
                {t(translations.pages.internationalOffice.courses.enroll())}
              </Button>
            );
          }
        },
      !iframe &&
        aclHelper?.isIncomingsAdmin() && {
          width: 90,
          headerName: t(translations.pages.internationalOffice.events.orderNumber()),
          field: 'orderNumber'
        },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.internationalOffice.events.eventName()),
        field: 'eventName',
        renderCell: params => {
          return (
            <div>
              <Tooltip title={params.row.eventName}>
                <Typography variant="body2">{params.row.eventName}</Typography>
              </Tooltip>
              <Tooltip title={params.row.meetingPoint}>
                <Typography variant="body2">
                  <LocationOn />
                  {params.row.meetingPoint}
                </Typography>
              </Tooltip>
            </div>
          );
        }
      },
      {
        width: 130,
        headerName: t(translations.pages.internationalOffice.events.date()),
        field: 'date',
        renderCell: params => {
          return (
            <div>
              <Typography variant="body2">
                {format(new Date(params.row.eventDate), 'MM/dd/yyyy')}
              </Typography>
              <Typography variant="body2">
                <AccessTime />
                {format(new Date(params.row.eventDate), 'hh:mm a', { locale: enUS })}
              </Typography>
            </div>
          );
        }
      },
      {
        headerName: t(translations.pages.internationalOffice.events.participants()),
        field: 'participants',
        width: 210,
        renderCell: params => {
          return (
            <div>
              {params.row.participants.map((participant, index) => (
                <Typography variant="body2" key={index}>
                  {t(
                    translations.pages.internationalOffice.events.participantsTypes[participant]()
                  )}
                </Typography>
              ))}
            </div>
          );
        }
      },
      {
        flex: 3,
        minWidth: 200,
        headerName: t(translations.pages.internationalOffice.events.contact()),
        field: 'contact',
        renderCell: params => {
          return (
            <div>
              <Tooltip title={params.row.eventOfficer}>
                <Typography variant="body2">{params.row.eventOfficer}</Typography>
              </Tooltip>
              <Tooltip title={params.row.eventOfficerEmail}>
                <Typography variant="body2">
                  <Email />
                  {params.row.eventOfficerEmail}
                </Typography>
              </Tooltip>
            </div>
          );
        }
      },
      {
        width: 150,
        headerName: t(translations.pages.internationalOffice.events.registrationDeadline()),
        field: 'registrationDeadline',
        renderCell: params => (
          <Typography variant="body2">
            {format(new Date(params.row.registrationDeadline), 'MM/dd/yyyy')}
          </Typography>
        )
      },
      {
        width: 80,
        headerName: t(translations.pages.internationalOffice.events.costs()),
        field: 'costs',
        renderCell: params => formatMonetaryValue(params.value)
      },
      {
        width: 130,
        headerName: t(translations.pages.internationalOffice.events.eventDescription()),
        field: 'eventDescription',
        renderCell: params => {
          return params.row.eventDescription ? (
            <Grid container justifyContent="center" className="ioEvent-item__eventDescription">
              <a
                href={`${import.meta.env.VITE_GATEWAY_URL}/event/${params.id}/description`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon className="far fa-file-pdf fa-3x" />
              </a>
            </Grid>
          ) : (
            <Grid container justifyContent="center">
              <Icon
                className="fas fa-ban fa-3x"
                style={{
                  color: '#ccc'
                }}
              />
            </Grid>
          );
        }
      },
      !iframe &&
        aclHelper?.isIncomingsAdmin() && {
          width: 100,
          headerName: t(translations.pages.internationalOffice.events.actions()),
          type: 'actions',
          field: 'actions',
          align: 'center',
          getActions: getIoAdminActions
        }
    ].filter(Boolean);
  }, [aclHelper, getIoAdminActions, handleEnroll, handleUnenroll, iframe, isEnrolled, t]);

  return (
    <>
      <ErrorBoundary boundaryTag="ioEventModalBoundary">
        <Modal
          open={editModalEvent !== null}
          onClose={(event, reason) => {
            if (reason && reason === 'backdropClick') return;
            setEditModalEvent(null);
          }}
          headline={t(translations.pages.internationalOffice.users.editIOUser())}
          onCancel={() => {
            setEditModalEvent(null);
          }}
          disabledCancel={disabledCancel}
        >
          <IOEvent
            closeHandler={() => setEditModalEvent(null)}
            setDisabledCancel={setDisabledCancel}
            event={editModalEvent}
          />
        </Modal>
      </ErrorBoundary>
      <ConfirmationDialog
        open={deleteModalOpen !== null}
        title={t(translations.pages.internationalOffice.events.deleteConfirm.title())}
        onClose={() => setDeleteModalOpen(null)}
        onConfirm={() => {
          handleDelete(deleteModalOpen);
          setDeleteModalOpen(null);
        }}
      >
        <div>{t(translations.pages.internationalOffice.events.deleteConfirm.message())}</div>
      </ConfirmationDialog>
      <GQLGuard
        query={[eventsQuery]}
        onError={error => {
          dispatch(
            AlertActions.error(
              `${t(translations.pages.internationalOffice.events.alerts.error.loadingEvents())}`
            )
          );
          navigate('/', { replace: true });
          return null;
        }}
      >
        {([eventsData]) => {
          const eventsCount = eventsData.ioEventsCount;
          return (
            <DataGrid
              slotProps={{
                toolbar: {
                  filters: {
                    filterConfig: filters,
                    onFilterChange: refetch
                  }
                }
              }}
              rows={eventsData.ioEvents}
              columns={columns.map(column => ({
                ...column,
                sortable: false
              }))}
              disableColumnFilter
              rowHeight={80}
              disableRowSelectionOnClick
              getRowClassName={params => isEnrolled(params.id) && 'enrolled'}
              paginationMode="server"
              // onPageSizeChange={handlePageSizeChange}
              // pageSize={pageSize}
              rowCount={eventsCount}
              paginationModel={{
                page: paginationModel.page,
                pageSize: paginationModel.pageSize
              }}
              onPaginationModelChange={pagModel => {
                handlePaginationChange(pagModel);
                handlePagination(null, pagModel.page);
              }}
              // onPageChange={(page, details) => handlePagination(details, page)}
            />
          );
        }}
      </GQLGuard>
    </>
  );
}
